const STORAGE_KEY = "guide.pendingRagTransfers";
const STALE_MS = 2 * 60 * 1000;

type PendingMap = Record<string, number>;

function readMap(): PendingMap {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      // migrate legacy string[] format
      const migrated: PendingMap = {};
      for (const id of parsed) {
        if (typeof id === "string" && id) migrated[id] = Date.now();
      }
      return migrated;
    }
    if (parsed && typeof parsed === "object") {
      const out: PendingMap = {};
      for (const [id, startedAt] of Object.entries(parsed as Record<string, unknown>)) {
        if (typeof startedAt === "number") out[id] = startedAt;
      }
      return out;
    }
    return {};
  } catch {
    return {};
  }
}

function writeMap(map: PendingMap) {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(map));
}

export function getPendingRagTransferIds(): Set<string> {
  return new Set(Object.keys(readMap()));
}

export function markRagTransferPending(id: string) {
  const map = readMap();
  map[id] = Date.now();
  writeMap(map);
}

export function clearRagTransferPending(id: string) {
  const map = readMap();
  if (!(id in map)) return;
  delete map[id];
  writeMap(map);
}

export function syncPendingRagTransfers(
  drafts: Array<{ id?: string; status?: string }>
): Set<string> {
  const map = readMap();
  const byId = new Map(drafts.filter((d) => d.id).map((d) => [d.id!, d]));
  let changed = false;
  const now = Date.now();

  for (const id of Object.keys(map)) {
    const draft = byId.get(id);
    if (!draft) {
      // Draft disappeared from list — drop pending marker.
      delete map[id];
      changed = true;
      continue;
    }
    if (draft.status === "INGESTED" || draft.status === "REJECTED" || draft.status === "INGESTING") {
      delete map[id];
      changed = true;
      continue;
    }
    if (draft.status === "DRAFT" && now - (map[id] ?? 0) > STALE_MS) {
      delete map[id];
      changed = true;
    }
  }

  if (changed) writeMap(map);
  return new Set(Object.keys(map));
}

export function isRagTransferInFlight(
  draft: { id?: string; status?: string },
  pendingIds: Set<string>,
  localTransferringId?: string | null
): boolean {
  if (!draft.id) return false;
  if (draft.status === "INGESTING") return true;
  if (localTransferringId === draft.id) return true;
  return pendingIds.has(draft.id);
}
